import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import SettingsCurrencyConversionModalsDeleteProfile from 'lumen/components/settings/currency-conversion/modals/delete-profile';
import SettingsCurrencyConversionModalsEditTitle from 'lumen/components/settings/currency-conversion/modals/edit-title';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';

export interface SettingsCurrencyConversionTablesCellsTdWithActionsSignature {
  Args: {
    cellData: CurrencyConversionExchangeRatesProfile;
    column: {
      isReadOnly?: boolean;
    };
  };
  Element: HTMLTableCellElement;
}

const ACTIONS = {
  EDIT: 'edit',
  DELETE: 'delete',
} as const;

type ActionOption = Option & {
  value: string;
};

export default class SettingsCurrencyConversionTablesCellsTdWithActions extends Component<SettingsCurrencyConversionTablesCellsTdWithActionsSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked showConfirmDeleteModal = false;
  @tracked showEditTitleModal = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    return [
      {
        label: this.intl.t('settings.currency_conversion.actions.edit_title'),
        value: ACTIONS.EDIT,
      },
      {
        label: this.intl.t('general.delete'),
        value: ACTIONS.DELETE,
        icon: 'bin' as const,
      },
    ];
  }

  @action handleCloseDeleteModal(): void {
    this.showConfirmDeleteModal = false;
  }

  @action handleCloseEditTitleModal(): void {
    this.showEditTitleModal = false;
  }

  @action handleOnChange(option: ActionOption): void {
    if (option.value === ACTIONS.EDIT) {
      this.showEditTitleModal = true;
    }

    if (option.value === ACTIONS.DELETE) {
      this.showConfirmDeleteModal = true;
    }
  }

  @action navigateToProfile(): void {
    const routeName = this.currencyConversion.isHotelLevel
      ? 'hotel.settings.currency-conversion.profile'
      : 'market.settings.currency-conversion.profile';

    this.usageAnalytics.track('Exchange Rate Profile Opened', {
      eventTrigger: 'profile-opened',
    });

    void this.router.transitionTo(routeName, this.args.cellData.id);
  }

  <template>
    <div>
      <PrismButton
        @buttonType="ghost"
        @icon={{if @column.isReadOnly "arrow-right" "edit-pencil"}}
        @onClick={{this.navigateToProfile}}
        @size="small"
        data-test-id="currency-conversion-profile-navigate"
      />
      {{#unless @column.isReadOnly}}
        <PrismComboSelectInput
          @onChange={{this.handleOnChange}}
          @options={{this.options}}
          @unsafe_triggerComponent={{component
            GeneralDropdownsTriggersTriggerDropdownIconButton
            buttonType="ghost"
            icon="kebab"
            small=true
          }}
          data-test-id="currency-conversion-profile-kebab"
        />
      {{/unless}}
    </div>

    {{! Edit Title Modal }}
    {{#if this.showEditTitleModal}}
      <SettingsCurrencyConversionModalsEditTitle
        @activeExchangeRatesProfile={{@cellData}}
        @onClose={{this.handleCloseEditTitleModal}}
      />
    {{/if}}

    {{! Delete Profile Modal }}
    {{#if this.showConfirmDeleteModal}}
      <SettingsCurrencyConversionModalsDeleteProfile
        @exchangeRatesProfile={{@cellData}}
        @onClose={{this.handleCloseDeleteModal}}
      />
    {{/if}}
  </template>
}
